import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import { useIsMobile } from '../hooks/useIsMobile';

export interface DetailsSidebarSection {
  /** Stable identifier for Accordion state. */
  value: string;
  label: string;
  /** Optional content rendered next to the label (e.g. a count badge). */
  extras?: React.ReactNode;
  /** Section body. Falls back to a "No {label} yet." placeholder when
   *  omitted so the open panel doesn't read as a layout bug. */
  body?: React.ReactNode;
}

interface DetailsSidebarProps {
  /** Sections rendered as collapsible accordion items. */
  sections: DetailsSidebarSection[];
  /** Default-open section values. Omitted = every section starts collapsed
   *  (matches Fluent's Accordion default). */
  defaultOpenValues?: string[];
  /** Section values that are forced open — the user can't collapse them
   *  while these are present (their accordion header still toggles, but the
   *  controlled state snaps them back open). Use for surfaces that must
   *  surface attention, e.g. Payments when there's outstanding balance. */
  forceOpenValues?: string[];
  /** Section values that open whenever this prop's contents change — useful
   *  for "react to an external state flip" (e.g. payment status transitions
   *  to due). Unlike forceOpenValues, the user CAN close these manually after
   *  they auto-open and the close sticks until the next external transition. */
  triggerOpenValues?: string[];
  /** When true, animates to 0 width (desktop) or slides off-screen
   *  (mobile overlay). Stays mounted so the transition runs both ways. */
  collapsed?: boolean;
  /** Width when expanded. Defaults to 300px to match the order details
   *  surface — the established right-side context panel width. When
   *  `resizable` is set this is the starting width before any saved override. */
  width?: number;
  /** Opt into a drag-to-resize handle on the panel's left edge (desktop only).
   *  The chosen width persists per `storageKey` across reloads. */
  resizable?: boolean;
  /** localStorage key the resized width is persisted under. Required for the
   *  width to survive reloads; without it resizing is session-only. */
  storageKey?: string;
  /** Resize bounds (desktop). Defaults: 240px / 560px. */
  minWidth?: number;
  maxWidth?: number;
  /** Hide the scroll indicator on the content area while keeping it scrollable
   *  (overflow stays auto). Use on surfaces where a persistent scrollbar reads
   *  as visual noise against the panel chrome. */
  hideScrollbar?: boolean;
  /** Opt into the mobile slide-in overlay. When false (default), the panel
   *  is simply hidden below 768px (legacy behavior) — use this only on
   *  surfaces that drive `collapsed` from a toggle and handle `onClose`. */
  mobileOverlay?: boolean;
  /** Invoked when the user dismisses the mobile overlay via its backdrop.
   *  Parents should set `collapsed` to true in response. */
  onClose?: () => void;
  /** Optional content pinned below the accordion at the bottom of the panel
   *  (e.g. a standalone toggle). Renders inside the scroll area after the last
   *  section, separated by a top divider. */
  footer?: React.ReactNode;
}

const useStyles = makeStyles({
  panel: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground3,
    height: '100%',
    overflowX: 'hidden',
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    transitionProperty: 'width, border-left-width, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
  },
  // While dragging the resize handle the width updates every mousemove —
  // killing the transition keeps the edge glued to the cursor instead of
  // easing behind it.
  dragging: {
    transitionDuration: '0ms',
  },
  // The scrollable content lives in its own child so the resize handle (a
  // sibling) can stay pinned to the panel's left edge regardless of scroll.
  scrollArea: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
  },
  // Scrollable but with no visible scrollbar (Firefox + Chromium/WebKit/Edge).
  scrollAreaNoBar: {
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  collapsedState: {
    width: '0px !important',
    borderLeftWidth: '0px',
  },
  // Mobile: a 300px right rail eats most of a 375px viewport, so instead of
  // sitting inline the panel becomes a fixed overlay that slides in from the
  // right over the content (dismissed via the backdrop below).
  mobileOverlay: {
    position: 'fixed',
    top: '48px',
    right: 0,
    bottom: 0,
    height: 'auto',
    width: 'min(360px, 88vw)',
    zIndex: 1200,
    boxShadow: tokens.shadow28,
  },
  mobileHidden: {
    transform: 'translateX(100%)',
    boxShadow: 'none',
    borderLeftWidth: '0px',
    pointerEvents: 'none',
  },
  // Legacy mobile behavior for surfaces that haven't opted into the overlay:
  // the panel just doesn't render on phones.
  mobileDisplayNone: {
    display: 'none',
  },
  // Full-screen scrim behind the mobile overlay; tap to dismiss.
  backdrop: {
    position: 'fixed',
    top: '48px',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1199,
  },
  // Drag-to-resize grip straddling the panel's left edge. Transparent until
  // hovered/active so it reads as part of the divider, not a control.
  resizeHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '6px',
    cursor: 'col-resize',
    zIndex: 3,
    backgroundColor: 'transparent',
    transitionProperty: 'background-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorBrandStroke1,
    },
  },
  resizeHandleActive: {
    backgroundColor: tokens.colorBrandStroke1,
  },
  // Fixed-width inner so children don't reflow during the width animation.
  inner: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      width: '100% !important',
    },
  },
  emptyBody: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  accordionItem: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  accordionHeader: {
    '& button': {
      backgroundColor: tokens.colorNeutralBackground1,
      paddingTop: '14px',
      paddingBottom: '14px',
      minHeight: 'unset',
      height: 'auto',
      fontWeight: tokens.fontWeightSemibold,
    },
    '& button[aria-expanded="true"]': {
      ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
  },
  accordionPanel: {
    paddingTop: '12px',
    paddingBottom: '16px',
  },
  headingWithExtras: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  footer: {
    ...shorthands.padding('14px', '12px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

/**
 * Right-side context panel used on detail surfaces (order details, customer
 * profile, etc.). Shell-only: hosts a stack of collapsible accordion sections
 * with a consistent panel container, divider, and surface color. Page-specific
 * content is passed in via `sections`.
 *
 * This is the default style for right-side context panels — filter panels
 * (left rail, with a "Clear all" footer) use `FiltersSidebar` instead.
 */
const DetailsSidebar: React.FC<DetailsSidebarProps> = ({
  sections,
  defaultOpenValues,
  forceOpenValues,
  triggerOpenValues,
  collapsed = false,
  width = 300,
  resizable = false,
  storageKey,
  minWidth = 240,
  maxWidth = 560,
  hideScrollbar = false,
  mobileOverlay = false,
  onClose,
  footer,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const clampWidth = useCallback(
    (w: number) => Math.min(maxWidth, Math.max(minWidth, w)),
    [minWidth, maxWidth],
  );
  // Persisted width override. Only consulted when `resizable` — every other
  // caller renders at the static `width` prop with no handle.
  const [storedWidth, setStoredWidth] = useState<number>(() => {
    if (resizable && storageKey && typeof window !== 'undefined') {
      const parsed = parseInt(window.localStorage.getItem(storageKey) ?? '', 10);
      if (!Number.isNaN(parsed)) return clampWidth(parsed);
    }
    return width;
  });
  const effectiveWidth = resizable ? storedWidth : width;
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const onHandleDown = (e: React.MouseEvent) => {
    if (!resizable || isMobile) return;
    e.preventDefault();
    dragStart.current = { x: e.clientX, width: effectiveWidth };
    setDragging(true);
  };

  // Drag lifecycle: listen on the window so the pointer can leave the 6px grip
  // mid-drag. The panel hugs the viewport's right edge, so moving the handle
  // left (smaller clientX) widens it. Cursor + selection are locked to the
  // body for the duration so the page doesn't highlight text under the drag.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const delta = dragStart.current.x - e.clientX;
      setStoredWidth(clampWidth(dragStart.current.width + delta));
    };
    const onUp = () => {
      setDragging(false);
      dragStart.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging, clampWidth]);

  // Persist once the drag settles (not on every mousemove).
  useEffect(() => {
    if (dragging || !resizable || !storageKey || typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, String(effectiveWidth));
  }, [dragging, resizable, storageKey, effectiveWidth]);

  // Controlled-mode kicks in when either force- or trigger- variants are
  // supplied — keeps the default uncontrolled behavior for every other caller.
  const useControlled = !!forceOpenValues || !!triggerOpenValues;
  const [openItems, setOpenItems] = useState<string[]>(() => {
    const initial = new Set<string>(defaultOpenValues ?? []);
    forceOpenValues?.forEach((v) => initial.add(v));
    triggerOpenValues?.forEach((v) => initial.add(v));
    return Array.from(initial);
  });
  // Whenever the forced set changes, fold the new values into the open set
  // so a section that becomes forced opens immediately.
  useEffect(() => {
    if (!useControlled) return;
    setOpenItems((prev) => {
      const next = new Set(prev);
      forceOpenValues?.forEach((v) => next.add(v));
      return Array.from(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpenValues?.join('|')]);
  // Edge-triggered open: when triggerOpenValues' contents change, fold the
  // new values into the open set. Unlike forceOpenValues, the onToggle handler
  // doesn't re-add these — so the user can close them after the auto-open
  // and the close persists until the trigger array changes again.
  useEffect(() => {
    if (!useControlled) return;
    if (!triggerOpenValues || triggerOpenValues.length === 0) return;
    setOpenItems((prev) => {
      const next = new Set(prev);
      triggerOpenValues.forEach((v) => next.add(v));
      return Array.from(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerOpenValues?.join('|')]);
  // Desktop animates width to 0 when collapsed. On mobile: opt-in surfaces
  // get a fixed overlay that slides off-screen when collapsed; others keep
  // the legacy "hidden on phones" behavior.
  const panelClass = isMobile
    ? (mobileOverlay
        ? mergeClasses(styles.panel, styles.mobileOverlay, collapsed && styles.mobileHidden)
        : mergeClasses(styles.panel, styles.mobileDisplayNone))
    : mergeClasses(styles.panel, dragging && styles.dragging, collapsed && styles.collapsedState);
  const showHandle = resizable && !isMobile && !collapsed;

  return (
    <>
      {isMobile && mobileOverlay && !collapsed && (
        <div className={styles.backdrop} onClick={onClose} aria-hidden />
      )}
    <div
      className={panelClass}
      style={{ width: isMobile ? undefined : `${effectiveWidth}px` }}
      aria-hidden={collapsed}
    >
      {showHandle && (
        <div
          className={mergeClasses(styles.resizeHandle, dragging && styles.resizeHandleActive)}
          onMouseDown={onHandleDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
        />
      )}
      <div className={mergeClasses(styles.scrollArea, hideScrollbar && styles.scrollAreaNoBar)}>
        <div className={styles.inner} style={{ width: isMobile ? undefined : `${effectiveWidth}px` }}>
          <Accordion
            multiple
            collapsible
            defaultOpenItems={useControlled ? undefined : defaultOpenValues}
            openItems={useControlled ? openItems : undefined}
            onToggle={useControlled ? ((_, data) => {
              const next = new Set(data.openItems as string[]);
              forceOpenValues?.forEach((v) => next.add(v));
              setOpenItems(Array.from(next));
            }) : undefined}
          >
            {sections.map((section) => (
              <AccordionItem key={section.value} value={section.value} className={styles.accordionItem}>
                <AccordionHeader expandIconPosition="end" className={styles.accordionHeader}>
                  <span className={styles.headingWithExtras}>
                    {section.label}
                    {section.extras}
                  </span>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  {section.body ?? (
                    <div className={styles.emptyBody}>No {section.label.toLowerCase()} yet.</div>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
    </div>
    </>
  );
};

export default DetailsSidebar;
